import type {ReactElement} from 'react';
import {useRef} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import styles from './media-controls-item.module.scss';
import {useScrollDistance} from '@Hooks/useScrollDistance';

export interface MediaControlsItemProps {
  id: string;
  icon?: string;
  text?: string;
  tag?: ReactElement<any> | null;
  tagContainerClassName?: string;
  className?: string;
  selectedClasses?: string;
  textColorClasses?: string;
  textPaddingClasses?: string;
  iconClasses?: string;
  isTextBold?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  isSelected?: boolean;
  showAsListItem?: boolean;
  isDisabled?: boolean;
  onClick?(id: string): void;
  customIconObject?: ReactElement<any>;
  customPopup?: ReactElement<any>;
}

export function MediaControlsItem({
  tag = null,
  text = '',
  icon = '',
  className = '',
  tagContainerClassName = '',
  selectedClasses = '',
  textColorClasses = '',
  textPaddingClasses = '',
  iconClasses = '',
  loadingText = '',
  isTextBold = false,
  isLoading = false,
  isSelected = false,
  showAsListItem = false,
  onClick = (): void => {},
  isDisabled = false,
  customIconObject = undefined,
  customPopup = undefined,
  ...props
}: MediaControlsItemProps): ReactElement<any> | null {
  const elementRef = useRef<HTMLLIElement>(null);
  const distance = useScrollDistance(elementRef);

  const getTextColorClasses = (): string => {
    if (isDisabled) {
      return 'color-extra-light';
    }

    if (textColorClasses && textColorClasses !== '') {
      return textColorClasses;
    }

    return isSelected ? 'content-primary' : 'content-sub-text';
  };

  const getTextPaddingClasses = (): string => {
    if (textPaddingClasses && textPaddingClasses !== '') {
      return textPaddingClasses;
    }

    return 'spacing-p-r-6 spacing-p-l-6 ';
  };

  const getIconClasses = (): string => {
    if (iconClasses && iconClasses !== '') {
      return iconClasses;
    }
    return '';
  };

  const getContent = (): ReactElement<any> => {
    const textVal = isLoading ? (loadingText ?? window.i18next.t('pmwjs_loading')) : text;
    return (
      <>
        {!isLoading ? (
          <>{customIconObject ?? <Icon icon={icon} className={getIconClasses()} size={showAsListItem ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_24} />}</>
        ) : (
          <CircularProgressLoader classes="_unmargin" size={showAsListItem ? LOADER_SIZE.SMALL : LOADER_SIZE.DEFAULT} color={LOADER_COLOR.DARK} />
        )}
        <Text
          title={textVal}
          val={textVal}
          className={`${showAsListItem ? styles.textMarginLeft : 'spacing-m-t-1 '} ${getTextPaddingClasses()} ${styles.text} ${getTextColorClasses()}`}
          size={TextSize.XXSMALL}
          bold={isTextBold}
        />
      </>
    );
  };

  const getTag = (): ReactElement<any> | null => {
    return tag ? <div className={`${showAsListItem ? '' : `${styles.gridTagContainer} spacing-m-r-1 spacing-m-l-1 spacing-m-t-1`} ${tagContainerClassName}`}>{tag}</div> : null;
  };

  const getSelectedStateClasses = (): string => {
    if (isSelected) {
      return selectedClasses === '' ? styles.selected : selectedClasses;
    }
    return '';
  };

  const getCustomPopup = (): ReactElement<any> | null => {
    if (customPopup === undefined) {
      return null;
    }

    const distancePercentage = distance * 100;
    let repositionTriangle = false;
    let delta;
    if (distancePercentage <= 25) {
      delta = 7;
    } else if (distancePercentage <= 50) {
      delta = 4;
    } else if (distancePercentage <= 75) {
      delta = -2;
    } else if (distancePercentage <= 85) {
      repositionTriangle = true;
      delta = -14;
    } else if (distancePercentage <= 90) {
      repositionTriangle = true;
      delta = -20;
    } else {
      repositionTriangle = true;
      delta = -25;
    }

    const top = Math.floor(distancePercentage + delta);
    return distance > 0 && distance < 1 ? (
      <div className={styles.customPopupContainer} style={{top: `${top}%`}}>
        {customPopup}
        <div className={`${styles.triangle} ${repositionTriangle ? styles.middle : ''}`}></div>
      </div>
    ) : null;
  };

  const showAsGridItem = (): ReactElement<any> => {
    return (
      <>
        <li
          onClick={(): void => {
            onClick(props.id);
          }}
          id={props.id}
          className={`${styles.baseContainer} flex-center spacing-p-b-0 hover-transition-all ${styles.gridContainer} ${className} ${isDisabled ? styles.isDisabled : ''} ${getSelectedStateClasses()}`}
          ref={elementRef}
        >
          <div className="flex-column-align-center">{getContent()}</div>
          {getTag()}
        </li>
        {getCustomPopup()}
      </>
    );
  };

  const showAsList = (): ReactElement<any> => {
    return (
      <>
        <li
          onClick={(): void => {
            if (!isLoading) {
              onClick(props.id);
            }
          }}
          id={props.id}
          className={`${styles.baseContainer} ${isSelected ? styles.selected : ''} ${isDisabled ? styles.isDisabled : ''} ${
            styles.listContainer
          } spacing-p-2 flex-row-justify-between flex-items-center hover-transition-all ${className}`}
          ref={elementRef}
        >
          <div className="flex-row-align-center">{getContent()}</div>
          {getTag()}
        </li>
        {getCustomPopup()}
      </>
    );
  };

  const showMediaItem = (): ReactElement<any> => {
    return showAsListItem ? showAsList() : showAsGridItem();
  };

  return showMediaItem();
}
